import { AUDIO_NOTE_RECORDING_STATE } from '../../../enums/audioNoteRecordingState';
import { isProtectedPostAudioStoragePath } from './audioNotePlayback';

interface IAudioNotePlaybackChromeVisibilityData {
    audioNoteEmpty: boolean,
    hasHydratedPlaybackSource?: boolean,
    readOnly: boolean,
    status: AUDIO_NOTE_RECORDING_STATE,
}

/**
 * Read-only notes should never route through the "new unsaved note" branch,
 * even when the Slate node still has a blank or placeholder file path.
 */
export function shouldTreatAudioNoteAsNew(data: {
    readOnly: boolean,
    filePath: string | null | undefined,
    autoRecordIdentifier: string | null | undefined,
}): boolean {
    const {
        readOnly,
        filePath,
        autoRecordIdentifier,
    } = data;

    if (readOnly) {
        return false;
    }

    return !filePath || filePath === autoRecordIdentifier;
}

/**
 * Preview readers should prefer the preview audio derivative for post-content
 * dictation notes so the embedded Slate node never falls back to the full file.
 */
export function buildPreviewAudioNoteFilePath(filePath: string): string {
    const pathSegments = filePath.split('/');
    const storageEntity = pathSegments[0];
    if (
        storageEntity !== 'posts'
        && storageEntity !== 'staging-posts'
    ) {
        return filePath;
    }

    const lastSlashIndex = filePath.lastIndexOf('/');
    const directory = lastSlashIndex >= 0
        ? filePath.slice(0, lastSlashIndex)
        : '';
    const fileName = lastSlashIndex >= 0
        ? filePath.slice(lastSlashIndex + 1)
        : filePath;
    const extensionIndex = fileName.lastIndexOf('.');
    const extension = extensionIndex >= 0
        ? fileName.slice(extensionIndex)
        : '';
    const baseName = extensionIndex >= 0
        ? fileName.slice(0, extensionIndex)
        : fileName;

    if (/-preview-[^-]+$/.test(baseName)) {
        return filePath;
    }

    const suffixStartIndex = baseName.lastIndexOf('-');
    if (
        suffixStartIndex <= 0
        || suffixStartIndex >= baseName.length - 1
    ) {
        return filePath;
    }

    const previewBaseName = `${baseName.slice(0, suffixStartIndex)}-preview${baseName.slice(suffixStartIndex)}`;
    return directory
        ? `${directory}/${previewBaseName}${extension}`
        : `${previewBaseName}${extension}`;
}

/**
 * Read-only notes may hydrate against a preview-specific media payload after
 * the embedded Slate node has already supplied the full-file duration. In that
 * case, trust the hydrated value so the visible timer matches the delivered
 * preview asset instead of the original source note.
 */
export function resolveHydratedAudioLengthMs(data: {
    currentDurationMs: number,
    hydratedDurationMs: number,
    readOnly: boolean,
}): number {
    const {
        currentDurationMs,
        hydratedDurationMs,
        readOnly,
    } = data;

    if (readOnly) {
        return hydratedDurationMs;
    }

    if (currentDurationMs > 0) {
        return currentDurationMs;
    }

    return hydratedDurationMs;
}

/**
 * Preview-backed read-only post dictation notes should not trust the embedded
 * Slate duration until the authoritative preview media payload supplies one.
 * Other notes can keep using the embedded saved duration as an immediate hint.
 */
export function resolvePersistedAudioNoteDurationMs(data: {
    embeddedDurationMs?: number | null,
    embeddedFilePath?: string | null,
    hydratedDurationMs?: number | null,
    readOnly: boolean,
    readOnlyUsesPreviewSource?: boolean,
    isPostContentDictation?: boolean,
}): number | undefined {
    const {
        embeddedDurationMs,
        embeddedFilePath,
        hydratedDurationMs,
        readOnly,
        readOnlyUsesPreviewSource = false,
        isPostContentDictation = false,
    } = data;

    if (Number.isFinite(hydratedDurationMs)) {
        return hydratedDurationMs as number;
    }

    if (
        readOnly
        && readOnlyUsesPreviewSource
        && (
            isPostContentDictation
            || isProtectedPostAudioStoragePath(embeddedFilePath)
        )
    ) {
        return undefined;
    }

    return Number.isFinite(embeddedDurationMs)
        ? embeddedDurationMs as number
        : undefined;
}

/**
 * ReaderView can know a note title and saved waveform metadata before playback
 * has a real source URL or blob. Keep truly empty notes hidden, but let saved
 * waveform chrome render while protected post-audio URL hydration continues.
 */
export function shouldRenderAudioNotePlaybackChrome(
    data: IAudioNotePlaybackChromeVisibilityData,
): boolean {
    const {
        audioNoteEmpty,
        hasHydratedPlaybackSource = false,
        readOnly,
        status,
    } = data;

    if (status === AUDIO_NOTE_RECORDING_STATE.idle) {
        return false;
    }

    if (!readOnly) {
        return true;
    }

    if (audioNoteEmpty && !hasHydratedPlaybackSource) {
        return false;
    }

    return hasHydratedPlaybackSource;
}

/**
 * Playback controls should stay visible for saved notes while hydration is in
 * flight so the reader can queue playback and watch the startup spinner.
 */
export function shouldRenderAudioNotePlaybackControls(
    data: IAudioNotePlaybackChromeVisibilityData,
): boolean {
    const {
        audioNoteEmpty,
        status,
    } = data;

    if (status !== AUDIO_NOTE_RECORDING_STATE.idle) {
        return true;
    }

    return !audioNoteEmpty;
}

/**
 * The action row follows the same rule as the visible controls: keep it
 * mounted for saved notes in playback states, even while playback data is
 * still hydrating, so speed changes can be queued ahead of playback start.
 */
export function shouldRenderAudioNoteActionRow(
    data: IAudioNotePlaybackChromeVisibilityData,
): boolean {
    const {
        audioNoteEmpty,
        readOnly,
        status,
    } = data;

    if (
        status !== AUDIO_NOTE_RECORDING_STATE.idle
        && status !== AUDIO_NOTE_RECORDING_STATE.pausedComplete
        && status !== AUDIO_NOTE_RECORDING_STATE.playing
    ) {
        return false;
    }

    if (status === AUDIO_NOTE_RECORDING_STATE.idle) {
        return !readOnly || !audioNoteEmpty;
    }

    return true;
}

/**
 * Read-only playback should prefer the resolved media-doc path, then fall back
 * to the Slate node path. Placeholder auto-record ids are never valid media
 * objects.
 */
export function resolveAudioNotePlaybackFilePath(data: {
    filePath: string | null | undefined,
    remoteFilePath: string | null | undefined,
    autoRecordIdentifier: string | null | undefined,
    readOnlyUsesPreviewSource?: boolean,
    isPostContentDictation?: boolean,
    requireRemoteFilePath?: boolean,
}): string | null {
    const {
        filePath,
        remoteFilePath,
        autoRecordIdentifier,
        readOnlyUsesPreviewSource = false,
        isPostContentDictation = false,
        requireRemoteFilePath = false,
    } = data;

    const playbackPath = requireRemoteFilePath
        ? remoteFilePath
        : (remoteFilePath || filePath);
    const candidatePath = (
        readOnlyUsesPreviewSource
        && isPostContentDictation
        && typeof playbackPath === 'string'
    )
        ? buildPreviewAudioNoteFilePath(playbackPath)
        : playbackPath;
    if (!candidatePath) {
        return null;
    }
    if (candidatePath === autoRecordIdentifier) {
        return null;
    }

    return candidatePath;
}
